// Requêtes des écrans de la lecture assistée (lot L7), clés sous « assistance ». Types de
// packages/contrats/src/assistance-documentaire.ts ; le serveur refait chaque contrôle (droit sur la pièce,
// configuration, autorisation explicite, machine d'états).
import type {
  ConfigurationAssistance,
  ConfigurationAssistanceSaisie,
  DemandeLecture,
  LectureAssistee,
  RevuePropositionSaisie,
} from '@cc/contrats';
import type { DocumentResume } from '@cc/contrats/ui-metier';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';

export const INVALIDER_ASSISTANCE: readonly (readonly unknown[])[] = [['assistance']];

const lire =
  <T>(chemin: string) =>
  ({ signal }: { signal: AbortSignal }) =>
    requete<T>(chemin, { signal });
const envoyer = <T>(methode: 'POST' | 'PUT', chemin: string, corps?: unknown) =>
  requete<T>(chemin, { methode, corps });

export const useConfiguration = () =>
  useQuery({
    queryKey: ['assistance', 'configuration'],
    queryFn: lire<ConfigurationAssistance>('/assistance-documentaire/configuration'),
    retry: false,
  });
export const useLectures = (actif = true) =>
  useQuery({
    queryKey: ['assistance', 'lectures'],
    queryFn: lire<{ elements: LectureAssistee[] }>('/assistance-documentaire/lectures'),
    retry: false,
    enabled: actif,
  });
export const useLecture = (id: string | undefined) =>
  useQuery({
    queryKey: ['assistance', 'lecture', id ?? ''],
    queryFn: lire<LectureAssistee>(`/assistance-documentaire/lectures/${id}`),
    enabled: Boolean(id),
    retry: false,
  });
export const useDocumentsLisibles = (actif: boolean) =>
  useQuery({
    queryKey: ['assistance', 'documents'],
    queryFn: ({ signal }) =>
      requete<{ elements: DocumentResume[] }>('/documents', {
        signal,
        parametres: { limite: 100 },
      }),
    enabled: actif,
    retry: false,
  });

export const configurer = (c: ConfigurationAssistanceSaisie) =>
  envoyer<ConfigurationAssistance>('PUT', '/assistance-documentaire/configuration', c);
export const demanderLecture = (c: DemandeLecture) =>
  envoyer<LectureAssistee>('POST', '/assistance-documentaire/lectures', c);
export const actualiser = (id: string) =>
  envoyer<LectureAssistee>('POST', `/assistance-documentaire/lectures/${id}/actualisation`);
export const relancer = (id: string) =>
  envoyer<LectureAssistee>('POST', `/assistance-documentaire/lectures/${id}/relance`);
export const annuler = (id: string) =>
  envoyer<LectureAssistee>('POST', `/assistance-documentaire/lectures/${id}/annulation`);
export const revoir = (id: string, proposition: string, c: RevuePropositionSaisie) =>
  envoyer<LectureAssistee>(
    'POST',
    `/assistance-documentaire/lectures/${id}/propositions/${proposition}/revue`,
    c,
  );
